import {
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputRightAddon,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  compositionGroupTemplatePriceApi,
  AutoUpdateTemplatePrice,
  AutoUpdateTemplatePriceAdditionalData,
  CompositionGroupTemplatePrice,
  EMPTY_AUTO_UPDATE_TEMPLATE_PRICE_OBJECT,
} from '../../../api/endpoints/compositionGroupTemplatePriceApi';
import { BranchRegion } from '../../../api/endpoints/branchRegionsApi';
import { TemplateRangeTier } from '../../../api/endpoints/metadata/templateRangeTiersApi';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import {
  AutoUpdateResponseCallback,
  Position,
} from '@texas/components/shared/autoUpdate/types';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface TemplatePriceCellProps {
  branchRegion: BranchRegion;
  templateRangeTier: TemplateRangeTier;
  compositionGroupPurchaseInfoId: number;
  existingPrice?: CompositionGroupTemplatePrice;
}

export function TemplatePriceCell({
  branchRegion,
  templateRangeTier,
  compositionGroupPurchaseInfoId,
  existingPrice,
}: TemplatePriceCellProps) {
  const { t } = useTranslation();
  const {
    registerAutoUpdate,
    watch,
    getValues,
    resetValue,
    form,
    register,
    triggerSubmit,
  } = useAutoUpdateForm<
    AutoUpdateTemplatePrice,
    CompositionGroupTemplatePrice,
    AutoUpdateTemplatePriceAdditionalData
  >({
    defaultValue: {
      purchasePrice: existingPrice?.purchasePrice ?? 0,
      salesPrice: existingPrice?.salesPrice ?? 0,
    },
    requestParams: [],
    namePrefix: `templatePrice-${branchRegion.id}-${templateRangeTier.id}`,
    autoUpdateRequest: compositionGroupTemplatePriceApi.autoUpdateTemplatePrice,
    fallbackMapObject: EMPTY_AUTO_UPDATE_TEMPLATE_PRICE_OBJECT,
    additionalData: {
      compositionGroupPurchaseInfoId,
      branchRegionId: branchRegion.id,
      templateRangeTierId: templateRangeTier.id,
    },
    onResponse,
  });

  function onResponse(
    data: AutoUpdateResponseCallback<
      CompositionGroupTemplatePrice,
      AutoUpdateTemplatePrice
    >,
  ) {
    const serverData = data.unmodifiedServerData;
    if (
      getValues('purchasePrice') !== serverData.purchasePrice ||
      getValues('salesPrice') !== serverData.salesPrice
    ) {
      resetValue('purchasePrice', serverData.purchasePrice);
      resetValue('salesPrice', serverData.salesPrice);
    }
  }

  // Update form values when existingPrice changes
  useEffect(() => {
    if (existingPrice) {
      if (getValues('purchasePrice') !== existingPrice.purchasePrice) {
        resetValue('purchasePrice', existingPrice.purchasePrice);
      }
      if (getValues('salesPrice') !== existingPrice.salesPrice) {
        resetValue('salesPrice', existingPrice.salesPrice);
      }
    }
  }, [existingPrice, getValues, resetValue]);

  const watchPurchasePrice = watch('purchasePrice');
  const watchSalesPrice = watch('salesPrice');

  const margin = useMemo(() => {
    const purchase = watchPurchasePrice ?? 0;
    const sales = watchSalesPrice ?? 0;
    if (purchase === 0) return 0;
    return ((sales - purchase) / purchase) * 100;
  }, [watchPurchasePrice, watchSalesPrice]);

  return (
    <form {...form}>
      <Flex
        bg="entityDirectory.raisedSurface"
        border="1px solid"
        borderColor="entityDirectory.border"
        borderRadius={0}
        p={4}
        flexDirection="column"
        gap={2}
      >
        <FormControl>
          <AutoUpdateWrapper
            autoUpdateProps={registerAutoUpdate}
            path="purchasePrice"
            position={Position.Right}
          >
            <Flex justifyContent="space-between" alignItems="start">
              <Text color="entityDirectory.mutedText" fontSize="sm" mr={2}>
                {t('variant.marginCalculations.purchase')}
              </Text>
              <InputGroup size="sm">
                <Flex flexDir="column" gap={0} alignItems="end">
                  <Input
                    textAlign="end"
                    variant="unstyled"
                    {...register('purchasePrice', {
                      required: true,
                      min: 0,
                      valueAsNumber: true,
                      onBlur: triggerSubmit,
                    })}
                    color="entityDirectory.text"
                    placeholder="0"
                    type="number"
                    step="0.0001"
                  />
                  <Tooltip
                    label={t('configuration.calculatedPriceTooltip', {
                      multiplier: templateRangeTier.costMultiplier.toFixed(2),
                    })}
                  >
                    <Text color="entityDirectory.mutedText" fontSize="sm">
                      {(
                        (watchPurchasePrice ?? 0) *
                        templateRangeTier.costMultiplier
                      ).toFixed(nrOfDecimals(watchPurchasePrice ?? 0))}
                    </Text>
                  </Tooltip>
                </Flex>

                <InputRightAddon h="fit-content" bg="transparent" border="none">
                  {t('general.usd')}
                </InputRightAddon>
              </InputGroup>
            </Flex>
          </AutoUpdateWrapper>
        </FormControl>

        <FormControl>
          <AutoUpdateWrapper
            autoUpdateProps={registerAutoUpdate}
            path="salesPrice"
            position={Position.Right}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Text color="entityDirectory.mutedText" fontSize="sm" mr={2}>
                {t('variant.marginCalculations.sales')}
              </Text>
              <InputGroup size="sm">
                <Input
                  textAlign="end"
                  variant="unstyled"
                  {...register('salesPrice', {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                    onBlur: triggerSubmit,
                  })}
                  color="entityDirectory.text"
                  placeholder="0"
                  type="number"
                  step="0.0001"
                />
                <InputRightAddon bg="transparent" border="none">
                  {t('general.usd')}
                </InputRightAddon>
              </InputGroup>
            </Flex>
          </AutoUpdateWrapper>
        </FormControl>

        <Flex justifyContent="flex-end" mt={1}>
          <Tag colorScheme={margin <= 0 ? 'red' : 'gray'}>
            {margin.toFixed(0)}%
          </Tag>
        </Flex>
      </Flex>
    </form>
  );
}

function nrOfDecimals(num: number) {
  const numStr = num.toString();

  const decimalSeparator = numStr.indexOf('.') >= 0 ? '.' : ',';

  if (numStr.includes(decimalSeparator)) {
    return numStr.split(decimalSeparator)[1].length;
  }
  return 0;
}
